import * as React from 'react';
import { useAuth } from '@/auth/AuthProvider';
import { asRows } from './rows';

/**
 * F62 (0157): the caller's OWN memberships an admin deactivated, `my_removed_memberships()`. Needs no
 * agency (a removed member has none), so it cannot sit on useLiveQuery. The read is a courtesy for the
 * no-agency screen: a failure is logged and reads as "none", never an error wall.
 */
export interface RemovedMembership {
  agencyId: string;
  agencyName: string;
  removedAt: string | null;
}

export function useMyRemovedMemberships(): { status: 'loading' | 'ready'; removed: RemovedMembership[] } {
  const { client, user, loading } = useAuth();
  const userId = user?.id ?? null;
  const [state, setState] = React.useState<{ status: 'loading' | 'ready'; removed: RemovedMembership[] }>({ status: 'loading', removed: [] });

  React.useEffect(() => {
    if (loading) return;
    if (!userId) {
      setState({ status: 'ready', removed: [] });
      return;
    }
    let active = true;
    void (async () => {
      const { data, error } = await client.rpc('my_removed_memberships');
      if (!active) return;
      if (error) {
        console.warn('useMyRemovedMemberships: my_removed_memberships failed', error);
        setState({ status: 'ready', removed: [] });
        return;
      }
      const removed = asRows(data)
        .filter((r) => typeof r.agency_id === 'string' && typeof r.agency_name === 'string')
        .map((r) => ({
          agencyId: r.agency_id as string,
          agencyName: r.agency_name as string,
          removedAt: typeof r.removed_at === 'string' ? r.removed_at : null,
        }));
      setState({ status: 'ready', removed });
    })();
    return () => {
      active = false;
    };
  }, [client, userId, loading]);

  return state;
}
